import React, { useState } from 'react';
import { Search, BookOpen, Clock, CheckCircle2, Play, Award, Sparkles, Eye, ShieldAlert, Calendar, CreditCard } from 'lucide-react';
import { COURSE_PERIODS, COURSE_CATEGORIES } from '../../data/coursesData';
import { localCourseResumen } from '../../utils/courseStructure';
import '../../styles/CursosVortex.css';

export const CourseCatalog = ({
  courses = [],
  isSubscribed = false,
  isAuthenticated = false,
  onSelectCourse,
  onOpenDetailModal,
  onEnroll,
  onOpenAuth,
  onOpenLector
}) => {
  const [selectedPeriod, setSelectedPeriod] = useState('p1');
  const [selectedCategory, setSelectedCategory] = useState('Todos');
  const [searchQuery, setSearchQuery] = useState('');

  const filteredCourses = courses.filter((course) => {
    const matchesCategory =
      selectedCategory === 'Todos' || course.category === selectedCategory;
    const matchesPeriod =
      !selectedPeriod || course.periodId === selectedPeriod || selectedPeriod === 'all';
    const matchesSearch =
      !searchQuery.trim() ||
      course.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.description.toLowerCase().includes(searchQuery.toLowerCase()) ||
      course.category.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesCategory && matchesPeriod && matchesSearch;
  });

  return (
    <div className="vx-cursos-container">

      {/* ── Hero Header ── */}
      <div className="vx-hero">
        <div>
          <div className="vx-hero-badge">
            <Sparkles className="w-4 h-4 text-[#c5a059]" />
            <span>Preparación Integral EXANI-II para Universidades Públicas y Autónomas 2026</span>
          </div>
          <h1>
            Plataforma de <span className="vx-hero-highlight">Cursos Vortex</span>
          </h1>
          <p className="vx-hero-desc">
            Preparación teórica integral con clases en video, quices interactivos, materiales en PDF y simuladores oficiales.
            Un solo acceso te brinda entrada directa y simultánea a los 4 cursos fundamentales del EXANI-II durante el periodo activo existente.
          </p>
        </div>

        <div className="vx-hero-actions">
          {!isAuthenticated ? (
            <button className="btn-vx-action" onClick={onOpenAuth}>
              🔐 Iniciar Sesión / Registrarse
            </button>
          ) : isSubscribed ? (
            <div className="vx-badge-subscribers" style={{ background: 'rgba(16, 185, 129, 0.15)', borderColor: 'rgba(16, 185, 129, 0.4)', color: '#34d399' }}>
              <CheckCircle2 className="w-4 h-4" />
              <span>Paquete Activo (4 Cursos Desbloqueados)</span>
            </div>
          ) : (
            <button
              className="btn-vx-action"
              onClick={() => { const first = courses[0]; if (first) onEnroll(first); }}
            >
              <CreditCard className="w-4 h-4" />
              <span>Adquirir Paquete Ciclo 2026 (4 Cursos) — $300 MXN</span>
            </button>
          )}
          {isAuthenticated && isSubscribed && onOpenLector && (
            <button className="btn-vx-action" onClick={onOpenLector}>
              <BookOpen className="w-4 h-4" />
              <span>📖 Lector de Temas</span>
            </button>
          )}
        </div>
      </div>

      {/* ── Periodos Anuales ── */}
      <div>
        <div className="vx-section-header">
          <h2 className="vx-section-title">
            <Calendar className="w-5 h-5" />
            <span>Periodos de Estudio en el Año</span>
          </h2>
          <span className="vx-section-badge">3 PERIODOS ANUALES</span>
        </div>

        <div className="vx-periods-grid">
          {COURSE_PERIODS.map((period) => {
            const isSelected = selectedPeriod === period.id;
            return (
              <div
                key={period.id}
                onClick={() => setSelectedPeriod(period.id)}
                className={`vx-period-card${isSelected ? ' active' : ''}`}
              >
                <div>
                  <div className="vx-period-card-top">
                    <span className="vx-period-short">{period.shortName}</span>
                    <span className={`vx-period-pill${period.active ? ' is-active' : ''}`}>
                      {period.badge}
                    </span>
                  </div>
                  <h3>{period.name}</h3>
                  <p>{period.description}</p>
                </div>
                <div className="vx-period-footer">
                  <span>Fechas: {period.months}</span>
                  <span className={isSelected ? 'selected' : ''}>
                    {isSelected ? '✓ Seleccionado' : 'Seleccionar'}
                  </span>
                </div>
              </div>
            );
          })}
        </div>

        {/* Alerta de Exámenes */}
        <div style={{ marginTop: '1rem' }}>
          <div className="vx-alert-banner">
            <div className="vx-alert-icon">
              <ShieldAlert className="w-5 h-5" />
            </div>
            <div>
              <h4>Fase Académica Ceneval y Universidades Públicas/Autónomas 2026</h4>
              <p>
                Durante el ciclo de admisión las evaluaciones se ejecutan directamente en la{' '}
                <strong style={{ color: '#fff' }}>plataforma Vortex EXANI-II</strong> con simuladores de 168 reactivos y banco de reactivos actualizado.
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* ── Filtros y Buscador ── */}
      <div className="vx-filters-bar">
        <div className="vx-cat-list">
          {COURSE_CATEGORIES.map((cat) => (
            <button
              key={cat}
              onClick={() => setSelectedCategory(cat)}
              className={`btn-vx-cat${selectedCategory === cat ? ' active' : ''}`}
            >
              <span>
                {cat === 'Todos' && '✨'}
                {cat === 'Pensamiento Matemático' && '🧮'}
                {cat === 'Comprensión Lectora' && '📖'}
                {cat === 'Redacción Indirecta' && '✍️'}
                {cat === 'Inglés' && '🇬🇧'}
              </span>
              <span>{cat}</span>
            </button>
          ))}
        </div>

        <div className="vx-search-container">
          <Search className="vx-search-icon" />
          <input
            type="text"
            className="vx-search-input"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Buscar tema, lección, módulo, reactivos..."
          />
          {searchQuery && (
            <button className="vx-search-clear" onClick={() => setSearchQuery('')}>✕</button>
          )}
        </div>
      </div>

      {/* ── Malla de Cursos ── */}
      {filteredCourses.length === 0 ? (
        <div style={{
          background: 'rgba(15, 32, 53, 0.6)',
          border: '1px solid rgba(197, 160, 89, 0.2)',
          borderRadius: '14px',
          padding: '3rem',
          textAlign: 'center',
          maxWidth: '480px',
          margin: '0 auto',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          gap: '1rem'
        }}>
          <div style={{ fontSize: '2rem' }}>🔍</div>
          <h3 style={{ fontFamily: 'var(--font-tactical)', color: '#fff', margin: 0, fontSize: '1rem' }}>
            No se encontraron cursos
          </h3>
          <p style={{ fontSize: '0.8rem', color: 'var(--text-secondary)', margin: 0 }}>
            Intenta cambiar los filtros de categoría o buscar con otra palabra clave.
          </p>
          <button
            className="btn-vx-action"
            onClick={() => { setSelectedCategory('Todos'); setSearchQuery(''); }}
          >
            Ver todos los cursos
          </button>
        </div>
      ) : (
        <div className="vx-courses-grid">
          {filteredCourses.map((course) => (
            <div key={course.id} className="vx-course-card">
              {/* Cover */}
              <div>
                <div className="vx-card-cover" onClick={() => onOpenDetailModal(course)}>
                  <img src={course.thumbnail} alt={course.title} />
                  <div className="vx-card-cover-overlay-grad" />
                  <span className="vx-card-cat-badge">{course.category}</span>
                  <span
                    className="vx-card-price-badge"
                    style={{
                      background: isSubscribed ? 'rgba(52, 211, 153, 0.2)' : 'rgba(197, 160, 89, 0.2)',
                      color: isSubscribed ? '#34d399' : 'var(--gold-matte)',
                      border: isSubscribed ? '1px solid #34d399' : '1px solid rgba(197, 160, 89, 0.4)',
                    }}
                  >
                    {isSubscribed ? '✓ Desbloqueado' : 'Paquete 4 Cursos'}
                  </span>

                  <div className="vx-card-hover-action">
                    <div className="vx-card-hover-btn">
                      <Eye className="w-4 h-4" />
                      <span>Ver Resumen Completo</span>
                    </div>
                  </div>
                </div>

                {/* Body */}
                <div className="vx-card-body">
                  <h3 onClick={() => onOpenDetailModal(course)}>{course.title}</h3>
                  <p>{course.description}</p>
                </div>
              </div>

              {/* Footer */}
              <div className="vx-card-footer">
                <div className="vx-card-specs">
                  <div className="vx-card-spec-item lessons">
                    <BookOpen />
                    <span>{course.resumen?.temas || localCourseResumen(course).temas} Temas · {course.resumen?.lecciones || localCourseResumen(course).lecciones} Lecciones</span>
                  </div>
                  <div className="vx-card-spec-item time">
                    <Clock />
                    <span>{course.resumen?.miniExamenes ?? localCourseResumen(course).miniExamenes} Mini Exámenes · {course.resumen?.examenes ?? localCourseResumen(course).examenes} Exámenes</span>
                  </div>
                </div>

                <div className="vx-card-actions-grid">
                  <button className="btn-card-resumen" onClick={() => onOpenDetailModal(course)}>
                    <Eye className="w-3.5 h-3.5" />
                    <span>Resumen</span>
                  </button>

                  {isSubscribed ? (
                    <button className="btn-card-enter" onClick={() => onSelectCourse(course)}>
                      <Play className="w-3.5 h-3.5" />
                      <span>Ingresar al Curso</span>
                    </button>
                  ) : (
                    <button className="btn-card-buy" onClick={() => onEnroll(course)}>
                      <CreditCard className="w-3.5 h-3.5" />
                      <span>Adquirir Paquete ($300)</span>
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* ── Banner Footer Sincronización ── */}
      <div className="vx-footer-banner">
        <div className="vx-footer-banner-left">
          <div className="vx-footer-badge-icon">
            <Award className="w-6 h-6" />
          </div>
          <div>
            <h4>Sincronización de Avance y Contenidos de Vortex EXANI-II</h4>
            <p>
              Todos los datos, cuestionarios y material de estudio están alineados con el temario oficial del EXANI-II
              y los lineamientos de admisión de universidades públicas y autónomas de México.
            </p>
          </div>
        </div>
      </div>

    </div>
  );
};
